import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Typography, Grid, Card, Avatar, Button, Modal, Badge, Tooltip } from "@mui/material";
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { colors } from "../components/Theme/WebTheme";
import moment from "moment/min/moment-with-locales";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from 'common';
import ImageViewerModal from '../components/ImageViewerModal';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DescriptionIcon from '@mui/icons-material/Description';
import SecurityIcon from '@mui/icons-material/Security';
import CircularLoading from "../components/CircularLoading";

function DriverCars() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const [data, setData] = useState([]);
  const carlistdata = useSelector((state) => state.carlistdata);
  const [activeCar, setActiveCar] = useState({});
  const [otherCars, setOtherCars] = useState([]);
  const navigate = useNavigate();
  const { state } = useLocation();

  const dispatch = useDispatch();
  const rootRef = useRef(null);
  const [profileModal, setProfileModal] = useState(false);
  const [viewerState, setViewerState] = useState({ open: false, src: '', title: '' });
  const [userData, setUserData] = useState();
  const [, setLoading] = useState(false);
  const { editCar } = api;
  const auth = useSelector((state) => state.auth);
  const role = auth?.profile?.usertype;

  const renderCarMessages = (car, fontSize = 16) => {
    if (!car) return null;
    const isApproved = car.approved === true;
    const msg = car.rejectionReason;
    const hasNewUpload = car.hasNewCarUpload || car.carDocUpdated;

    // If driver viewing (if ever viewed by driver)
    if (role === 'driver') {
      if (isApproved) {
        return (
          <Typography style={{ color: '#555', textAlign: "center", fontSize: fontSize, fontWeight: "bold" }}>
            {t("no_action_required", "No action required")}
          </Typography>
        );
      }
      if (hasNewUpload) {
        return (
          <Typography style={{ color: '#d32f2f', textAlign: "center", fontSize: fontSize, fontWeight: "bold" }}>
            {t("car_pending_approval", { defaultValue: "Your Car pending approval" })}
          </Typography>
        );
      }
      const reasons = Array.isArray(msg)
        ? msg.filter(Boolean)
        : typeof msg === 'string'
        ? msg.split('\n').map(s => s.trim()).filter(Boolean)
        : [msg].filter(Boolean);

      if (reasons.length === 0) {
        return (
          <Typography style={{ color: '#d32f2f', textAlign: "center", fontSize: fontSize, fontWeight: "bold" }}>
            {t("car_pending_approval", { defaultValue: "Your Car pending approval" })}
          </Typography>
        );
      }

      const formattedText = reasons.map(r => t(r, r)).join(', ');
      return (
        <Typography style={{ color: '#d32f2f', textAlign: "center", fontSize: fontSize, fontWeight: "bold", padding: '0 8px' }}>
          {formattedText}
        </Typography>
      );
    }

    // In Admin Panel:
    if (isApproved) {
      return (
        <Typography style={{ color: '#555', textAlign: "center", fontSize: fontSize, fontWeight: "bold" }}>
          {t("no_action_required", "No action required")}
        </Typography>
      );
    }

    const reasons = Array.isArray(msg)
      ? msg.filter(Boolean)
      : typeof msg === 'string'
      ? msg.split('\n').map(s => s.trim()).filter(Boolean)
      : [msg].filter(Boolean);

    if (reasons.length === 0) {
      if (hasNewUpload) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Typography style={{ color: '#555', textAlign: "center", fontSize: fontSize, fontWeight: "bold" }}>
              {t("no_action_required", "No action required")}
            </Typography>
            <Tooltip title={t('new_car_document_uploaded', 'New car document or info updated by driver')}>
              <Badge color="error" variant="dot">
                <NotificationsActiveIcon style={{ color: '#ff9800', fontSize: 18 }} />
              </Badge>
            </Tooltip>
          </div>
        );
      }
      return (
        <Typography style={{ color: '#555', textAlign: "center", fontSize: fontSize, fontWeight: "bold" }}>
          {t("no_action_required", "No action required")}
        </Typography>
      );
    }

    const formattedText = reasons.map(r => t(r, r)).join(', ');

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap', padding: '0 8px' }}>
        <Typography style={{ color: '#d32f2f', textAlign: "center", fontSize: fontSize, fontWeight: "bold" }}>
          {formattedText}
        </Typography>
        {hasNewUpload && (
          <Tooltip title={t('new_car_document_uploaded', 'New car document or info updated by driver')}>
            <Badge color="error" variant="dot">
              <NotificationsActiveIcon style={{ color: '#ff9800', fontSize: 18 }} />
            </Badge>
          </Tooltip>
        )}
      </div>
    );
  };

  const handleProfileModal = (e) => {
    setProfileModal(false);
  }
  const onClickImage = (rowData) => {
    setProfileModal(true);
    setUserData(rowData);
  }

  useEffect(() => {
    if (carlistdata.cars) {
      setData(carlistdata.cars.filter((item) => item.driver === id.toString()));
    } else {
      setData([]);
    }
  }, [carlistdata.cars, id]);

  useEffect(() => {
    if (data) {
      setActiveCar(data.filter((item) => item.active === true)[0]);
      setOtherCars(data.filter((item) => item.active !== true));
    } else {
      setActiveCar([]);
      setOtherCars([]);
    }
  }, [data]);

  return (
    carlistdata.loading ? <CircularLoading /> :
      <>
        <div
          style={{
            display: "flex",
            flexDirection: isRTL === "rtl" ? "row-reverse" : "row",
            justifyContent: isRTL === "rtl" ? "flex-end" : "flex-start",
          }}
        >
          <Button
            variant="text"
            onClick={() => {
              navigate('/users/1',
                { state: { pageNo: state?.pageNo } })
            }}
            sx={{ mb: 2 }}
          >
            <Typography
              style={{
                margin: "10px 10px 0 5px",
                textAlign: isRTL === "rtl" ? "right" : "left",
                fontWeight: "bold",
                color: colors.MAIN_COLOR,
              }}
            >
              {`<<- ${t("go_back")}`}
            </Typography>
          </Button>
        </div>

        <Grid container spacing={1} sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}>
          <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
            <>
              <Card
                style={{
                  borderRadius: "5px",
                  backgroundColor: colors.MAIN_COLOR,
                  marginTop: 5,
                  marginBottom: 5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="h5"
                  style={{
                    textAlign: "center",
                    color: colors.WHITE,
                  }}
                >
                  {t("active_car")}
                </Typography>
              </Card>
            <Card
              style={{
                borderRadius: "19px",
                backgroundColor: "#fff",
                minHeight: 100,
                padding: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
              }}
            >
              {activeCar ? (
                <Grid container direction="column" gap={2} padding={3}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginBottom: -10 }}>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={() => navigate(`/cars/updatecar/${activeCar.id}`, { state: { pageNo: state?.pageNo, fromDriverCars: true, driverId: id }})}
                      size="small"
                    >
                      {t('update_car', 'UPDATE CAR')}
                    </Button>
                  </div>
                  <Grid
                    item
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {activeCar?.car_image ? (
                      <div onClick={() => onClickImage(activeCar)} style={{cursor: 'pointer'}}>
                        <Avatar
                          alt="Id Image"
                          src={activeCar.car_image}
                          sx={{
                            width: 300,
                            height: 'auto',
                            borderRadius: "19px",
                          }}
                          variant="square"
                        />
                      </div>
                    ) : (
                      <div onClick={() => onClickImage(activeCar)} style={{cursor: 'pointer'}}>
                        <Avatar sx={{ width: 300, height: 200 }} variant="square">
                          {t("car_image")}
                        </Avatar>
                      </div>
                    )}
                  </Grid>
                  <Grid
                    item
                    style={{
                      display: "flex",
                      justifyContent: "space-around",
                      alignItems: "center",
                    }}
                  >
                    <Card
                      style={{
                        borderRadius: "19px",
                        backgroundColor:
                          activeCar?.approved === true
                            ? colors.GREEN
                            : colors.RED,
                        minWidth: "40%",
                        minHeight: 10,
                        padding: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        style={{
                          fontSize: 16,
                          textAlign: "center",
                          color: "white",
                        }}
                      >
                        {activeCar?.approved
                          ? t("approved")
                          : t("not_approved")}
                      </Typography>
                    </Card>
                    <Card
                      style={{
                        borderRadius: "19px",
                        backgroundColor: colors.MAIN_COLOR,
                        minWidth: "40%",
                        minHeight: 10,
                        padding: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        style={{
                          fontSize: 16,
                          textAlign: "center",
                          color: colors.WHITE,
                        }}
                      >
                        {activeCar?.carType}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item>
                    <Grid
                      container
                      spacing={1}
                      sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                    >
                      <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "0 15px 15px 0"
                                : "15px 0 0 15px",
                            backgroundColor: colors.MAIN_COLOR,
                            minHeight: 60,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 5,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "white",
                              textAlign: "center",
                              fontSize: 15,
                              fontWeight: "bold"
                            }}
                          >
                            {t("car_messages", "Car Messages")}
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "15px 0 0 15px "
                                : "0 15px 15px 0",
                            backgroundColor: colors.WHITE,
                            minHeight: 60,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 5,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                            {renderCarMessages(activeCar)}
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid
                      container
                      spacing={1}
                      sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                    >
                      <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "0 15px 15px 0"
                                : "15px 0 0 15px",
                            backgroundColor: colors.MAIN_COLOR,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 10,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "white",
                              textAlign: "center",
                              fontSize: 16,
                            }}
                          >
                            {t("createdAt")}
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "15px 0 0 15px "
                                : "0 15px 15px 0",
                            backgroundColor: colors.WHITE,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 10,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "Black",
                              textAlign: "center",
                              fontSize: 18,
                              fontWeight: "bold",
                            }}
                          >
                            {activeCar?.createdAt
                              ? moment(activeCar?.createdAt).format("lll")
                              : null}
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid
                      container
                      spacing={1}
                      sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                    >
                      <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "0 15px 15px 0"
                                : "15px 0 0 15px",
                            backgroundColor: colors.MAIN_COLOR,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 10,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "white",
                              textAlign: "center",
                              fontSize: 16,
                            }}
                          >
                            {t("category")}
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "15px 0 0 15px "
                                : "0 15px 15px 0",
                            backgroundColor: colors.WHITE,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 10,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "Black",
                              textAlign: "center",
                              fontSize: 18,
                              fontWeight: "bold",
                            }}
                          >
                            {activeCar?.category ? t(`${activeCar?.category}`) : t('no_data_available')}
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid
                      container
                      spacing={1}
                      sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                    >
                      <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "0 15px 15px 0"
                                : "15px 0 0 15px",
                            backgroundColor: colors.MAIN_COLOR,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 2,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: colors.WHITE,
                              textAlign: "center",
                              fontSize: 16,
                            }}
                          >
                            {t("vehicle_model_name")}
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "15px 0 0 15px "
                                : "0 15px 15px 0",
                            backgroundColor: colors.WHITE,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 2,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: colors.BLACK,
                              textAlign: "center",
                              fontSize: 18,
                              fontWeight: "bold",
                            }}
                          >
                            {activeCar?.vehicleMake ? activeCar.vehicleMake : t('no_data_available')}
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid
                      container
                      spacing={1}
                      sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                    >
                      <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "0 15px 15px 0"
                                : "15px 0 0 15px",
                            backgroundColor: colors.MAIN_COLOR,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 2,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "white",
                              textAlign: "center",
                              fontSize: 16,
                            }}
                          >
                            {t("vehicle_model_no")}
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "15px 0 0 15px "
                                : "0 15px 15px 0",
                            backgroundColor: colors.WHITE,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 2,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "Black",
                              textAlign: "center",
                              fontSize: 18,
                              fontWeight: "bold",
                            }}
                          >
                            {activeCar?.vehicleModel ? activeCar?.vehicleModel : t('no_data_available')}
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item>
                    <Grid
                      container
                      spacing={1}
                      sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                    >
                      <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                        <Card
                          style={{
                            fontSize: 16,
                            textAlign: "center",
                            color: "white",
                          }}
                        >
                          <Typography
                            style={{
                              color: "white",
                              textAlign: "center",
                              fontSize: 16,
                            }}
                          >
                            {t("carcapacity")}
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "15px 0 0 15px "
                                : "0 15px 15px 0",
                            backgroundColor: colors.WHITE,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 2,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "Black",
                              textAlign: "center",
                              fontSize: 18,
                              fontWeight: "bold",
                            }}
                          >
                            {activeCar?.capacity ? `${activeCar?.capacity} ${activeCar.unit_type?activeCar.unit_type:""}` : t('no_data_available')}
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid
                      container
                      spacing={1}
                      sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                    >
                      <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "0 15px 15px 0"
                                : "15px 0 0 15px",
                            backgroundColor: colors.MAIN_COLOR,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 10,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "white",
                              textAlign: "center",
                              fontSize: 16,
                            }}
                          >
                            {t("vehicle_no")}
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "15px 0 0 15px "
                                : "0 15px 15px 0",
                            backgroundColor: colors.WHITE,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 10,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "Black",
                              textAlign: "center",
                              fontSize: 18,
                              fontWeight: "bold",
                            }}
                          >
                            {activeCar?.vehicleNumber ? activeCar?.vehicleNumber : t('no_data_available')}
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid
                      container
                      spacing={1}
                      sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                    >
                      <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "0 15px 15px 0"
                                : "15px 0 0 15px",
                            backgroundColor: colors.MAIN_COLOR,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 10,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "white",
                              textAlign: "center",
                              fontSize: 16,
                            }}
                          >
                            {t("car_color", "Car Color")}
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "15px 0 0 15px "
                                : "0 15px 15px 0",
                            backgroundColor: colors.WHITE,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 10,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "Black",
                              textAlign: "center",
                              fontSize: 18,
                              fontWeight: "bold",
                            }}
                          >
                            {activeCar?.car_color ? activeCar?.car_color : t('no_data_available')}
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid
                      container
                      spacing={1}
                      sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                    >
                      <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "0 15px 15px 0"
                                : "15px 0 0 15px",
                            backgroundColor: colors.MAIN_COLOR,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 10,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "white",
                              textAlign: "center",
                              fontSize: 16,
                            }}
                          >
                            {t("production_year", "Production Year")}
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "15px 0 0 15px "
                                : "0 15px 15px 0",
                            backgroundColor: colors.WHITE,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 10,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "Black",
                              textAlign: "center",
                              fontSize: 18,
                              fontWeight: "bold",
                            }}
                          >
                            {activeCar?.car_production_year ? activeCar?.car_production_year : t('no_data_available')}
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid
                      container
                      spacing={1}
                      sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                    >
                      <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "0 15px 15px 0"
                                : "15px 0 0 15px",
                            backgroundColor: colors.MAIN_COLOR,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 10,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "white",
                              textAlign: "center",
                              fontSize: 16,
                            }}
                          >
                            {t("other_info")}
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Card
                          style={{
                            borderRadius:
                              isRTL === "rtl"
                                ? "15px 0 0 15px "
                                : "0 15px 15px 0",
                            backgroundColor: colors.WHITE,
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: 10,
                            boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                          }}
                        >
                          <Typography
                            style={{
                              color: "Black",
                              textAlign: "center",
                              fontSize: 18,
                              fontWeight: "bold",
                            }}
                          >
                            {activeCar?.other_info ? activeCar?.other_info : t('no_data_available')}
                          </Typography>
                        </Card>
                      </Grid>
                  </Grid>
                </Grid>
                </Grid>
              ) : null}
            </Card>
          </>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
          <>
            <Card
              style={{
                borderRadius: "5px",
                backgroundColor: colors.MAIN_COLOR,
                marginTop: 5,
                marginBottom: 5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="h5"
                style={{
                  textAlign: "center",
                  color: "white",
                }}
              >
                {t("other_cars")}
              </Typography>
            </Card>

            <Card
              style={{
                borderRadius: "19px",
                backgroundColor: "#fff",
                minHeight: 100,
                padding: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 10,
                boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
              }}
            >
              {otherCars?.map((item, idx) => (
                <div key={item.id} style={{ width: "90%", marginBottom: 50 }}>
                  <Card
                    style={{
                      borderRadius: "19px",
                      backgroundColor: "#fff",
                      width: "100%",
                      minHeight: 100,
                      padding: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0px 2px 5px ${colors.THIRDCOLOR}`,
                    }}
                  >
                    <Grid container direction="column">
                      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginBottom: -10 }}>
                        <Button 
                          variant="contained" 
                          color="primary" 
                          onClick={() => navigate(`/cars/updatecar/${item.id}`, { state: { pageNo: state?.pageNo, fromDriverCars: true, driverId: id }})}
                          size="small"
                        >
                          {t('update_car', 'UPDATE CAR')}
                        </Button>
                      </div>
                      <Grid item>
                        <Grid container>
                          <Grid
                            item
                            xs={12}
                            sm={12}
                            md={12}
                            lg={6}
                            xl={6}
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {item?.car_image ? (
                              <div onClick={() => onClickImage(item)} style={{cursor: 'pointer'}}>
                                <Avatar
                                  alt="Id Image"
                                  src={item.car_image}
                                  sx={{
                                    width: 250,
                                    height: 'auto',
                                    borderRadius: "19px",
                                  }}
                                  variant="square"
                                />
                              </div>
                            ) : (
                              <div onClick={() => onClickImage(item)} style={{cursor: 'pointer'}}>
                                <Avatar
                                  sx={{ width: 250, height: 180 }}
                                  variant="square"
                                >
                                  {t("car_image")}
                                </Avatar>
                              </div>
                            )}
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            sm={12}
                            md={12}
                            lg={6}
                            xl={6}
                            style={{
                              display: "flex",
                              justifyContent: "space-around",
                              alignItems: "center",
                              marginTop: 5,
                            }}
                          >
                            <Card
                              style={{
                                borderRadius: "19px",
                                backgroundColor:
                                  item?.approved === true
                                    ? colors.GREEN
                                    : colors.RED,
                                minWidth: "40%",
                                minHeight: 10,
                                padding: 10,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Typography
                                style={{
                                  fontSize: 14,
                                  textAlign: "center",
                                  color: "white",
                                }}
                              >
                                {item?.approved
                                  ? t("approved")
                                  : t("not_approved")}
                              </Typography>
                            </Card>
                            <Card
                              style={{
                                borderRadius: "19px",
                                backgroundColor: colors.MAIN_COLOR,
                                minWidth: "40%",
                                minHeight: 10,
                                padding: 10,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Typography
                                style={{
                                  fontSize: 14,
                                  textAlign: "center",
                                  color: colors.WHITE,
                                }}
                              >
                                {item?.carType}
                              </Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={12} style={{ marginTop: 8 }}>
                            <Grid container spacing={1} sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
                              <Grid item xs={5} sm={5} md={5} lg={5} xl={5}>
                                <Card style={{ borderRadius: isRTL === "rtl" ? "0 12px 12px 0" : "12px 0 0 12px", backgroundColor: colors.MAIN_COLOR, minHeight: 45, display: "flex", alignItems: "center", justifyContent: "center", padding: 4 }}>
                                  <Typography style={{ color: "white", textAlign: "center", fontSize: 13, fontWeight: "bold" }}>
                                    {t("car_messages", "Car Messages")}
                                  </Typography>
                                </Card>
                              </Grid>
                              <Grid item xs={7} sm={7} md={7} lg={7} xl={7}>
                                <Card style={{ borderRadius: isRTL === "rtl" ? "12px 0 0 12px" : "0 12px 12px 0", backgroundColor: colors.WHITE, minHeight: 45, display: "flex", alignItems: "center", justifyContent: "center", padding: 4 }}>
                                  {renderCarMessages(item, 13)}
                                </Card>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        style={{
                          marginTop: 10,
                        }}
                      >
                        <Grid
                          container
                          spacing={1}
                          sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                        >
                          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <Typography
                              style={{
                                color: "black",
                                textAlign: isRTL === "rtl" ? "right" : "left",
                                fontSize: 16,
                                padding:
                                  isRTL === "rtl" ? "0 20px 0 0" : "0 0 0 20px",
                              }}
                            >
                              {t("createdAt")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <Typography
                              style={{
                                color: "Black",
                                textAlign: "center",
                                fontSize: 18,
                                fontWeight: "bold",
                              }}
                            >
                              {item?.createdAt
                                ? moment(item?.createdAt).format("lll")
                                : null}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <Grid
                          container
                          spacing={1}
                          sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                        >
                          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <Typography
                              style={{
                                color: "black",
                                textAlign: isRTL === "rtl" ? "right" : "left",
                                fontSize: 16,
                                padding:
                                  isRTL === "rtl" ? "0 20px 0 0" : "0 0 0 20px",
                              }}
                            >
                              {t("category")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <Typography
                              style={{
                                color: "Black",
                                textAlign: "center",
                                fontSize: 18,
                                fontWeight: "bold",
                              }}
                            >
                              {item?.category ? t(`${item?.category}`) : t('no_data_available')}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <Grid
                          container
                          spacing={1}
                          sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                        >
                          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <Typography
                              style={{
                                color: "black",
                                textAlign: isRTL === "rtl" ? "right" : "left",
                                fontSize: 16,
                                padding:
                                  isRTL === "rtl" ? "0 20px 0 0" : "0 0 0 20px",
                              }}
                            >
                              {t("carcapacity")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <Typography
                              style={{
                                color: "Black",
                                textAlign: "center",
                                fontSize: 18,
                                fontWeight: "bold",
                              }}
                            >
                              {item?.capacity ? `${item?.capacity} ${item.unit_type?item.unit_type:""}` : t('no_data_available')}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <Grid
                          container
                          spacing={1}
                          sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                        >
                          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <Typography
                              style={{
                                color: "black",
                                textAlign: isRTL === "rtl" ? "right" : "left",
                                fontSize: 16,
                                padding:
                                  isRTL === "rtl" ? "0 20px 0 0" : "0 0 0 20px",
                              }}
                            >
                              {t("vehicle_model_name")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <Typography
                              style={{
                                color: "Black",
                                textAlign: "center",
                                fontSize: 18,
                                fontWeight: "bold",
                              }}
                            >
                              {item?.vehicleMake ? item?.vehicleMake : t('no_data_available')}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <Grid
                          container
                          spacing={1}
                          sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                        >
                          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <Typography
                              style={{
                                color: "black",
                                textAlign: isRTL === "rtl" ? "right" : "left",
                                fontSize: 16,
                                padding:
                                  isRTL === "rtl" ? "0 20px 0 0" : "0 0 0 20px",
                              }}
                            >
                              {t("vehicle_model_no")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <Typography
                              style={{
                                color: "Black",
                                textAlign: "center",
                                fontSize: 18,
                                fontWeight: "bold",
                              }}
                            >
                              {item?.vehicleModel ? item?.vehicleModel : t('no_data_available')}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <Grid
                          container
                          spacing={1}
                          sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                        >
                          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <Typography
                              style={{
                                color: "black",
                                textAlign: isRTL === "rtl" ? "right" : "left",
                                fontSize: 16,
                                padding:
                                  isRTL === "rtl" ? "0 20px 0 0" : "0 0 0 20px",
                              }}
                            >
                              {t("vehicle_reg_no")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <Typography
                              style={{
                                color: "Black",
                                textAlign: "center",
                                fontSize: 18,
                                fontWeight: "bold",
                              }}
                            >
                              {item?.vehicleNumber ? item?.vehicleNumber : t('no_data_available')}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <Grid
                          container
                          spacing={1}
                          sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr", }}
                        >
                          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <Typography
                              style={{
                                color: "black",
                                textAlign: isRTL === "rtl" ? "right" : "left",
                                fontSize: 16,
                                padding:
                                  isRTL === "rtl" ? "0 20px 0 0" : "0 0 0 20px",
                              }}
                            >
                              {t("other_info")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <Typography
                              style={{
                                color: "Black",
                                textAlign: "center",
                                fontSize: 18,
                                fontWeight: "bold",
                              }}
                            >
                              {item?.other_info ? item?.other_info : t('no_data_available')}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Card>
                </div>
              ))}
            </Card>
          </>
        </Grid>
      </Grid>
      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={profileModal}
        onClose={handleProfileModal}
        style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}
        container={() => rootRef.current}
      >
        <div style={{ maxWidth: 680, width: '92vw', backgroundColor: '#fff', padding: '24px', borderRadius: 16, boxShadow: '0 10px 30px rgba(0,0,0,0.15)', outline: 'none' }}>
          <Typography variant="h6" style={{ marginBottom: 16, fontWeight: 700, textAlign: 'center' }}>
            {t('car_images')}
          </Typography>
          <div style={{ display: "flex", flexDirection: "row", gap: 16, alignItems: "flex-start", width: "100%" }}>
            {(() => {
              const renderDocSlot = (title, field, fileInputId, defaultIcon, isInsurance = false) => {
                const fileOrUrl = userData ? userData[field] : null;
                const imageSrc = fileOrUrl 
                  ? (fileOrUrl instanceof File ? URL.createObjectURL(fileOrUrl) : (typeof fileOrUrl === 'string' && fileOrUrl.trim() !== '' ? fileOrUrl : null)) 
                  : null;
                // When car is approved, only Insurance remains editable
                const isApprovedCar = userData?.approved === true;
                const isSlotDisabled = isApprovedCar && !isInsurance;
                const canEdit = Boolean(userData) && !isSlotDisabled;

                return (
                  <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                      {title}
                    </Typography>

                    {imageSrc ? (
                      <div
                        style={{
                          width: '100%',
                          height: '160px',
                          borderRadius: 10,
                          border: '1px solid #e2e8f0',
                          backgroundColor: '#f1f5f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          position: 'relative',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          opacity: isSlotDisabled ? 0.5 : 1,
                          filter: isSlotDisabled ? 'grayscale(60%)' : 'none',
                        }}
                        onClick={() => setViewerState({ open: true, src: imageSrc, title })}
                      >
                        <img 
                          src={imageSrc} 
                          alt={title} 
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) {
                              e.target.nextSibling.style.display = 'flex';
                            }
                          }}
                        />
                        <div style={{ display: 'none', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', padding: 10 }}>
                          {defaultIcon}
                          <Typography variant="caption" style={{ color: '#64748b', marginTop: 4, textAlign: 'center' }}>
                            {t('no_image_available', 'Image failed to load')}
                          </Typography>
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '160px',
                          borderRadius: 10,
                          border: '2px dashed #cbd5e1',
                          backgroundColor: '#f8fafc',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 10,
                          cursor: canEdit ? 'pointer' : 'default',
                          boxSizing: 'border-box',
                          opacity: isSlotDisabled ? 0.5 : 1,
                        }}
                        onClick={() => {
                          if (canEdit) {
                            const fileInput = document.getElementById(fileInputId);
                            if (fileInput) fileInput.click();
                          }
                        }}
                      >
                        {defaultIcon}
                        <Typography variant="caption" style={{ color: '#64748b', marginTop: 6, fontWeight: 500, textAlign: 'center' }}>
                          {t('no_document', 'No Document Uploaded')}
                        </Typography>
                      </div>
                    )}

                    {canEdit ? (
                      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", width: "100%", marginTop: 10 }}>
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id={fileInputId}
                          type="file"
                          onChange={(e) => {
                            const f = e.target.files[0];
                            if (f) {
                              let finalData = { ...userData };
                              finalData[field] = f;
                              setUserData(finalData);
                            }
                          }}
                        />
                        <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                          <label htmlFor={fileInputId}>
                            <Button variant="outlined" component="span" style={{ fontSize: 11, padding: '4px 12px' }}>
                              {fileOrUrl instanceof File ? fileOrUrl.name : (imageSrc ? t('change') : t('upload', 'Upload'))}
                            </Button>
                          </label>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              };

              return (
                <>
                  {renderDocSlot(t('car_image', 'Car Image'), 'car_image', 'car-button-file', <DirectionsCarIcon sx={{ fontSize: 44, color: '#94a3b8' }} />)}
                  {renderDocSlot(t('car_registration', 'Car Registration'), 'car_registration_image', 'reg-button-file', <DescriptionIcon sx={{ fontSize: 44, color: '#94a3b8' }} />)}
                  {renderDocSlot(`${t('car_insurance', 'Car Insurance')} (Optional)`, 'car_insurance_image', 'ins-button-file', <SecurityIcon sx={{ fontSize: 44, color: '#94a3b8' }} />, true)}
                </>
              );
            })()}
          </div>
          
          <div style={{display: 'flex', justifyContent: 'center', marginTop: 20}}>
              <Button variant="contained" color="success" onClick={() => {
                  setLoading(true);
                  dispatch(editCar({ ...userData, hasNewCarUpload: true, carDocUpdated: true }, "UpdateImage"));
                  setProfileModal(false); 
                  setTimeout(()=>{
                    setLoading(false); 
                  },3000);
              }} style={{ marginRight: 10 }}>
                {t('save')}
              </Button>
              <Button variant="outlined" color="error" onClick={handleProfileModal}>
                {t('cancel')}
              </Button>
          </div>
        </div>
      </Modal>
      <ImageViewerModal
        open={viewerState.open}
        src={viewerState.src}
        title={viewerState.title}
        onClose={() => setViewerState({ open: false, src: '', title: '' })}
      />
    </>
  );
}

export default DriverCars;
